import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinusCircle } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import { asMoneyString, formatCurrencySymbol } from "../../utils/formatters";

export function getColor(percent) {
  if (typeof percent == "string") {
    return "hsl(213, 80%,  42%)";
  }

  const p = (percent * 100) / 100;
  const luma = 58;
  const increase = 1.42 + p - 0.82;

  if (p > 1) {
    return "rgb(18, 85, 166)";
  }

  return "hsl(213, 80%, " + luma / increase + "%)";
}

export function getPercent(value) {
  return Math.floor(value * 100).toString() + "%";
}

export function getValue(propValue) {
  if (typeof propValue == "string") {
    return propValue;
  }
  return propValue <= 0 ? 0 : propValue;
}

export function calculateWidth(val) {
  const strVal = typeof val === "string" ? val : String(val);

  let newVal = strVal.split("%");
  if (newVal[0] < 100) {
    return newVal[0] + "%";
  }

  if (strVal === Infinity + "%" || newVal[0] === "NaN") {
    return "0%";
  }

  return "100%";
}

const ProjectedBar = (props) => {
  const { isAnimated = true, isPercent = true, showForecast = true } = props;
  const [state, setState] = useState({
    value: "0%",
    propValue: 0,
    projectedValue: "0%",
    propProjectedValue: 0
  });

  const animateBar = () => {
    const propValue = getValue(props.value);
    const propProjectedValue = getValue(props.projectedValue);

    setState({
      propValue,
      propProjectedValue,
      value: getPercent(propValue),
      projectedValue: getPercent(propProjectedValue)
    });
  };

  useEffect(() => {
    if (
      state.propValue === getValue(props.value) &&
      state.propProjectedValue === getValue(props.projectedValue)
    ) {
      return;
    }

    animateBar();
  }, [props.value, props.projectedValue]);

  const getBarValue = (value) => {
    return isPercent ? (isFinite(value) ? (value * 100).toFixed() : 0) : value;
  };

  const getWidth = (value) => {
    return props.fillWidth ? "100%" : calculateWidth(value);
  };

  const getDataTip = (label, value) => {
    const symbol = formatCurrencySymbol(props.metricTitle, props.currencySymbol);

    return props.defaultTooltip
      ? `${label}: ${symbol ? symbol : ""}${asMoneyString(value)}`
      : props.tooltip
      ? props.tooltip
      : null;
  };

  const getData = () => {
    return {
      value: getBarValue(state.propValue),
      projected: getBarValue(state.propProjectedValue),
      symbol: isPercent ? "%" : ""
    };
  };

  const renderStackedBar = () => {
    const { value, projected, symbol } = getData();
    return (
      <div className="bar" style={{ ...styles.barStacked, top: showForecast ? "-8px" : "3px" }}>
        <div className="bar-wrapper" style={styles.barWrapperStacked}>
          <div
            className="bar-value"
            style={{
              ...styles.barValue,
              width: getWidth(value),
              backgroundColor: getColor(state.propValue),
              transition: isAnimated ? "width 0.5s" : "none",
              position: "absolute",
              zIndex: state.propValue < state.propProjectedValue ? 2 : 1
            }}
            data-testid="stacked-bar-value"
          >
            <p
              className="value-title"
              style={{ ...styles.valueTitleStacked, color: "white" }}
              data-tooltip-place="left"
              data-tooltip-content={getDataTip("Current", props.currentVal)}
              data-testid="stacked-value-title"
            >
              {state.propValue * 100 === Infinity ? (
                <span>0%</span>
              ) : (
                <span>
                  {isAnimated ? <CountUp end={value} duration={2.5} /> : value}
                  {symbol}
                </span>
              )}
            </p>
          </div>
        </div>
        {showForecast && (
          <div className="bar-wrapper" style={styles.barWrapperStacked}>
            <div
              className="bar-projected"
              style={{
                ...styles.barProjected,
                width: getWidth(state.projectedValue),
                backgroundColor: "#fd6847",
                transition: isAnimated ? "width 0.5s" : "none",
                position: "absolute",
                zIndex: state.propProjectedValue < state.propValue ? 2 : 1
              }}
              data-testid="stacked-bar-projected"
            >
              <p
                className="value-title"
                style={{ ...styles.valueTitleStacked, ...styles.valueProjected }}
                data-tooltip-place="right"
                data-tooltip-content={getDataTip("Projected", props.currentValPacing)}
                data-testid="stacked-value-title-2"
              >
                <span>
                  {isAnimated ? <CountUp end={projected} duration={2.5} /> : projected}
                  {symbol}
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWithoutStackedBar = () => {
    const { value, projected, symbol } = getData();

    return (
      <div className="bar" style={styles.bar}>
        <div className="bar-wrapper" style={styles.barWrapper}>
          <div
            className="bar-value"
            style={{
              ...styles.barValue,
              width: getWidth(value),
              backgroundColor: getColor(state.propValue),
              transition: isAnimated ? "width 0.5s" : "none",
              position: "absolute",
              zIndex: state.propValue < state.propProjectedValue ? 2 : 1
            }}
            data-testid="bar-value"
          >
            <p
              className="value-title"
              style={{ ...styles.valueTitle, color: "white" }}
              data-tooltip-place="left"
              data-tooltip-content={getDataTip("Current", props.currentVal)}
              data-testid="value-title"
            >
              <span>
                {isAnimated ? <CountUp end={value} duration={2.5} /> : value}
                {symbol}
              </span>
            </p>
          </div>
          <div
            className="bar-projected"
            style={{
              ...styles.barProjected,
              width: getWidth(state.projectedValue),
              backgroundColor: "#fd6847",
              transition: isAnimated ? "width 0.5s" : "none",
              position: "absolute",
              zIndex: state.propProjectedValue < state.propValue ? 2 : 1
            }}
            data-testid="bar-projected"
          >
            <p
              className="value-title"
              style={{ ...styles.valueTitle, ...styles.valueProjected }}
              data-tooltip-place="right"
              data-tooltip-content={getDataTip("Projected", props.currentValPacing)}
              data-testid="value-title-2"
            >
              <span>
                {isAnimated ? <CountUp end={projected} duration={2.5} /> : projected}
                {symbol}
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ width: "450xpx" }}>
      {props.defaultTooltip || props.tooltip ? <Tooltip anchorSelect=".value-title" float /> : null}

      <p className="location-name" style={styles.locationName} data-testid="location-name">
        {props.locationName}
      </p>
      <p className="metric-title" style={styles.metricTitle} data-testid="metric-title">
        {props.metricTitle}
      </p>

      {props.isStacked ? renderStackedBar() : renderWithoutStackedBar()}

      {!props.hideMinus && (
        <div className="minus" style={styles.minus} data-testid="minus">
          <FontAwesomeIcon
            className="minus-icon"
            icon={faMinusCircle}
            style={{ color: "#8ac934" }}
            onClick={props.onMinusClick}
          />
        </div>
      )}
    </div>
  );
};

const styles = {
  metricTitle: {
    color: "#7f838e",
    paddingBottom: "5px",
    display: "inline-flex",
    margin: 0,
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: "12px"
  },
  locationName: {
    color: "rgb(135, 145, 159)",
    lineHeight: "12px",
    fontSize: "12px",
    margin: 0,
    position: "relative",
    top: "5px",
    width: "350px"
  },
  bar: {
    width: "97%",
    float: "left"
  },
  barStacked: {
    width: "97%",
    float: "left",
    position: "relative",
    top: "-8px"
  },
  minus: {
    width: "3%",
    paddingLeft: "3px",
    float: "left",
    cursor: "pointer",
    right: "-17px",
    position: "absolute"
  },
  barWrapper: {
    width: "100%",
    height: "23px",
    overflow: "hidden",
    position: "relative",
    marginBottom: "10px"
  },
  barWrapperStacked: {
    width: "100%",
    height: "18px",
    overflow: "hidden",
    position: "relative",
    marginBottom: "1px"
  },
  barValue: {
    width: "0%"
  },
  barProjected: {
    width: "0%"
  },
  valueTitle: {
    textAlign: "right",
    fontWeight: 600,
    paddingRight: "5px",
    fontSize: "12px"
  },
  valueTitleStacked: {
    textAlign: "right",
    fontWeight: 600,
    paddingRight: "5px",
    fontSize: "12px",
    position: "relative",
    top: "-2px"
  },
  valueProjected: {
    opacity: 1,
    color: "white"
  }
};

export default ProjectedBar;
